import React, { ReactNode, useMemo, useRef } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Grid, createColumnHelper } from '@components-library/Grid/Grid';
import { KeyMapKeys, keyMap } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import { getFormattedNumber } from 'utils/helper';
import styles from './gridTotals.style.module.scss';

/**
 * @info Interface is not precise
 * It includes dynamic columns that represent random number of currencies
 */
interface IGridTotalsByCurrencyColumns {
  property: string;
}

interface IGenericSummary {
  currency: string;
  count: number;
  main: boolean;
}

interface ITotals {
  totalCount: number;
  summary: IGenericSummary[];
}

interface IRowLabels {
  [summaryExtraProperty: string]: string;
}

const columnHelper = createColumnHelper<IGridTotalsByCurrencyColumns>();

interface IGridTotalsByCurrencyProps {
  totalData?: ITotals | null;
  rowLabelsConfig: IRowLabels;
}

export const GridTotalsByCurrency = ({ totalData, rowLabelsConfig }: IGridTotalsByCurrencyProps) => {
  const { i18n } = useTranslation();
  const memoizedRowLabels = useRef(rowLabelsConfig);
  const rowLabels = memoizedRowLabels.current;

  const summary = useMemo(() => totalData?.summary ?? [], [totalData]);

  const TOTAL_KEY = 'total';

  const mainCurrency = summary.find((value) => value.main)?.currency;

  const gridData = useMemo(
    () =>
      Object.keys(rowLabels).map((property) => {
        return {
          property: rowLabels[property as keyof typeof rowLabels],
          ...summary.reduce((acc, value) => {
            // @ts-ignore
            acc[value.main ? TOTAL_KEY : value.currency] = getFormattedNumber(value[property], { precision: 2 });
            return acc;
          }, {}),
        };
      }),
    [summary, rowLabels],
  );

  const sortedColumnKeys = useMemo(
    () =>
      summary
        .map((value) => (value.main ? TOTAL_KEY : value.currency))
        .sort((keyA, keyB) => {
          if (keyA === TOTAL_KEY) {
            return 1;
          } else {
            return keyA > keyB ? 1 : -1;
          }
        }),
    [summary],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('property', { cell: (info) => i18n.t(info.getValue() as KeyMapKeys), header: () => '' }),
      ...sortedColumnKeys.map((currency) =>
        columnHelper.accessor(currency as keyof IGridTotalsByCurrencyColumns, {
          cell: (info) => info.getValue(),
          header: () =>
            currency === TOTAL_KEY
              ? i18n.t(keyMap.tableShowTotalsFieldsTotalInCurrency, { currency: mainCurrency })
              : currency,
        }),
      ),
    ],
    [sortedColumnKeys, i18n, mainCurrency],
  );

  return summary.length === 0 ? null : (
    <GridTotalsByCurrencyFormContextProvider>
      <Grid
        classes={{ tableWrapper: cn(styles.gridWrapperTotalsByCurrency, styles.totals), table: styles.table }}
        columns={columns}
        data={gridData}
      />
      {mainCurrency && <p className={styles.note}>{i18n.t(keyMap.tableQuotesNote, { currency: mainCurrency })}</p>}
    </GridTotalsByCurrencyFormContextProvider>
  );
};

const GridTotalsByCurrencyFormContextProvider = ({ children }: { children: ReactNode }) => {
  const hasFormContext = Boolean(useFormContext());
  const methods = useForm();

  if (!hasFormContext) {
    return <FormProvider {...methods}>{children}</FormProvider>;
  }
  return children;
};
